
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import { getAllMeals } from "../redux/actions/meals";

import {
  Loader,
  MealsCard,
  Pagination,
} from "../components/utils";

import {
  Section,
  Fotter,
  Header,
} from "../components/layout";

import { useStateContext } from "../context/ContextProvider";
import { media } from "../server";

import {
  FiGrid,
  FiList,
  FiColumns,
} from "react-icons/fi";

import { BiRestaurant } from "react-icons/bi";

function AllMeals() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    isLoading,
    Allmeals,
    pagination,
  } = useSelector((state) => state.Meals);

  const { currentDir } = useStateContext();

  const location = useLocation();
  const { category } = location.state || {};
  const categoryId = category?._id;

  const totalPages = pagination?.totalPages || 0;

  const [page, setPage] = useState(0);
  const [limit] = useState(16);

  // Main display mode
  const [viewMode, setViewMode] = useState("grid");

  // Mobile grid columns
  const [mobileColumns, setMobileColumns] =
    useState("one");

  // Sorting
  const [sortOrder, setSortOrder] = useState("");

  useEffect(() => {
    if (categoryId) {
      dispatch(
        getAllMeals(
          page,
          limit,
          categoryId,
          undefined,
          undefined,
          sortOrder
        )
      );
    }
  }, [
    page,
    limit,
    categoryId,
    sortOrder,
    dispatch,
  ]);

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
    setPage(0);
  };

  const handleOneColumnView = () => {
    setViewMode("grid");
    setMobileColumns("one");
  };

  const handleTwoColumnsView = () => {
    setViewMode("grid");
    setMobileColumns("two");
  };

  const handleGridView = () => {
    setViewMode("grid");
  };

  const handleListView = () => {
    setViewMode("list");
  };

  const containerVariants = {
    hidden: {
      opacity: 0,
    },

    visible: {
      opacity: 1,

      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: {
      y: 40,
      opacity: 0,
    },

    visible: {
      y: 0,
      opacity: 1,

      transition: {
        type: "spring",
        stiffness: 100,
        damping: 14,
      },
    },
  };

  const compactCard =
    viewMode === "grid" &&
    mobileColumns === "two";

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Header
        active={4}
        DropActive={1}
      />

      {/* Hero Section */}
      <motion.section
        className="
          relative
          w-full
          overflow-hidden
          h-[500px]
          lg:h-[650px]
        "
        initial={{
          scale: 1.08,
        }}
        animate={{
          scale: 1,
        }}
        transition={{
          duration: 1.2,
          ease: "easeOut",
        }}
      >
        <img
          src={`${media}${category?.Image}`}
          className="
            w-full
            h-full
            object-cover
          "
          alt={
            currentDir === "ltr"
              ? category?.titleEn
              : category?.titleAr
          }
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/15" />

        <div className="absolute top-0 start-0 w-[420px] h-[420px] bg-color-1/20 rounded-full blur-3xl" />

        <motion.div
          className="
            absolute
            bottom-0
            left-0
            right-0
            p-6
            md:p-12
            lg:p-20
          "
          initial={{
            y: 100,
            opacity: 0,
          }}
          animate={{
            y: 0,
            opacity: 1,
          }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: "easeOut",
          }}
        >
          <div className="container">
            <motion.div
              className="
                inline-flex
                items-center
                gap-2
                bg-color-1/20
                backdrop-blur-md
                rounded-full
                px-4
                py-2
                mb-4
                border
                border-color-1/20
              "
              initial={{
                x: currentDir === "rtl" ? 50 : -50,
                opacity: 0,
              }}
              animate={{
                x: 0,
                opacity: 1,
              }}
              transition={{
                delay: 0.5,
              }}
            >
              <BiRestaurant className="text-color-1" />

              <span className="text-color-1 text-sm font-kufi">
                {t("signature dishes")}
              </span>
            </motion.div>

            <h1 className="h1 text-white mb-4 md:mb-6 max-w-3xl">
              {currentDir === "ltr"
                ? category?.titleEn
                : category?.titleAr}
            </h1>

            <p className="body-1 text-white/80 max-w-2xl">
              {currentDir === "ltr"
                ? category?.textEn
                : category?.textAr}
            </p>
          </div>
        </motion.div>
      </motion.section>

      {isLoading ? (
        <div className="min-h-[50vh] flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <Section>
          <div
            id="meals-section"
            className="container py-12 lg:py-20"
          >
            {/* View Toggle, Count and Sort */}
            <motion.div
              className="
                flex
                flex-col
                xl:flex-row
                xl:items-center
                justify-between
                gap-5
                mb-12
                p-4
                md:p-6
                rounded-[2rem]
                border
                border-color-1/10
                bg-white/80
                dark:bg-back-darksecondary/80
                backdrop-blur-xl
                shadow-xl
              "
              initial={{
                opacity: 0,
                y: -20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.5,
              }}
            >
              {/* Count and view buttons */}
              <div
                className="
                  flex
                  flex-col
                  sm:flex-row
                  sm:items-center
                  justify-between
                  xl:justify-start
                  gap-4
                "
              >
                <div className="flex items-center gap-3">
                  <div
                    className="
                      w-12
                      h-12
                      rounded-xl
                      bg-color-1/10
                      flex
                      items-center
                      justify-center
                    "
                  >
                    <BiRestaurant className="text-color-1 text-xl" />
                  </div>

                  <div>
                    <span className="text-n-5 dark:text-n-3 body-2">
                      {t("showing")}{" "}
                      <span className="font-bold text-color-1">
                        {Allmeals?.length || 0}
                      </span>{" "}
                      {t("items")}
                    </span>
                  </div>
                </div>

                <div className="hidden sm:block h-8 w-px bg-gray-200 dark:bg-white/10" />

                {/* View Controls */}
                <div
                  className="
                    flex
                    items-center
                    gap-1.5
                    p-1.5
                    rounded-xl
                    bg-back-secondary
                    dark:bg-back-darktertiary
                  "
                >
                  {/* One column on mobile */}
                  <button
                    type="button"
                    onClick={handleOneColumnView}
                    className={`
                      md:hidden
                      w-10
                      h-10
                      rounded-lg
                      flex
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "grid" &&
                        mobileColumns === "one"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-n-6 dark:text-n-3 hover:bg-color-1/10 hover:text-color-1"
                      }
                    `}
                    aria-label={t("one column view")}
                    title={t("one column view")}
                  >
                    <FiList size={18} />
                  </button>

                  {/* Two columns on mobile */}
                  <button
                    type="button"
                    onClick={handleTwoColumnsView}
                    className={`
                      md:hidden
                      w-10
                      h-10
                      rounded-lg
                      flex
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "grid" &&
                        mobileColumns === "two"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-n-6 dark:text-n-3 hover:bg-color-1/10 hover:text-color-1"
                      }
                    `}
                    aria-label={t("two columns view")}
                    title={t("two columns view")}
                  >
                    <FiColumns size={18} />
                  </button>

                  {/* Tablet and desktop grid */}
                  <button
                    type="button"
                    onClick={handleGridView}
                    className={`
                      hidden
                      md:flex
                      w-10
                      h-10
                      rounded-lg
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "grid"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-n-6 dark:text-n-3 hover:bg-color-1/10 hover:text-color-1"
                      }
                    `}
                    aria-label={t("grid view")}
                    title={t("grid view")}
                  >
                    <FiGrid size={18} />
                  </button>

                  {/* List */}
                  <button
                    type="button"
                    onClick={handleListView}
                    className={`
                      w-10
                      h-10
                      rounded-lg
                      flex
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "list"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-n-6 dark:text-n-3 hover:bg-color-1/10 hover:text-color-1"
                      }
                    `}
                    aria-label={t("list view")}
                    title={t("list view")}
                  >
                    <FiList size={18} />
                  </button>
                </div>
              </div>

              {/* Sort */}
              <div
                className="
                  flex
                  flex-col
                  sm:flex-row
                  sm:items-center
                  gap-3
                "
              >
                <span className="text-n-5 dark:text-n-3 body-2 whitespace-nowrap">
                  {t("sort by")}:
                </span>

                <select
                  value={sortOrder}
                  onChange={handleSortChange}
                  className="
                    w-full
                    sm:w-auto
                    bg-white
                    dark:bg-back-darktertiary
                    border
                    border-color-1/10
                    rounded-xl
                    px-4
                    py-3
                    text-sm
                    text-tcolor-4
                    dark:text-white
                    font-kufi
                    cursor-pointer
                    focus:outline-none
                    focus:ring-2
                    focus:ring-color-1
                  "
                >
                  <option value="">
                    {t("default")}
                  </option>

                  <option value="asc">
                    {t("price low high")}
                  </option>

                  <option value="desc">
                    {t("price high low")}
                  </option>
                </select>
              </div>
            </motion.div>

            {/* Meals Grid/List */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`${viewMode}-${mobileColumns}-${sortOrder}`}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit={{
                  opacity: 0,
                }}
                className={
                  viewMode === "grid"
                    ? `
                      grid
                      ${
                        mobileColumns === "two"
                          ? "grid-cols-2 gap-3"
                          : "grid-cols-1 gap-6"
                      }
                      md:grid-cols-2
                      md:gap-7
                      lg:grid-cols-3
                      lg:gap-8
                    `
                    : "flex flex-col gap-6"
                }
              >
                {Allmeals?.map((meal) => (
                  <motion.div
                    key={meal._id}
                    variants={itemVariants}
                    layout
                    className="min-w-0"
                  >
                    <MealsCard
                      meals={meal}
                      category={category}
                      viewMode={viewMode}
                      compact={compactCard}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Empty State */}
            {(!Allmeals ||
              Allmeals.length === 0) && (
              <motion.div
                className="text-center py-20"
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
              >
                <div
                  className="
                    w-24
                    h-24
                    mx-auto
                    mb-6
                    rounded-full
                    flex
                    items-center
                    justify-center
                    bg-color-1/10
                    text-5xl
                  "
                >
                  🍽️
                </div>

                <h3 className="h4 text-n-6 dark:text-n-3 mb-2">
                  {t("no dishes found")}
                </h3>

                <p className="body-2 text-n-5 dark:text-n-4">
                  {t("try another category")}
                </p>
              </motion.div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                className="mt-16"
                initial={{
                  opacity: 0,
                  y: 30,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: 0.4,
                }}
              >
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  setPage={setPage}
                />
              </motion.div>
            )}
          </div>
        </Section>
      )}

      <Fotter />
    </>
  );
}

export default AllMeals;

